import * as fundingAPI from "app/api/fundingApi";
import * as nuveiApi from "app/api/nuveiApi";
import TextInput from "app/components/FormInputs/components/TextInput/TextInput";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { NuveiHelpers } from "app/helpers/nuveiHelpers";
import { UserType } from "app/types/userType";
import {
  FundingTypes,
  NuveiPaymentMethods,
  NuveiTransactionStatus,
} from "app/utils/const";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import ENV from "../../../../../../../../config";
import { LoadNuveiScript } from "../LoadNuveiScript/LoadNuveiScript";
import TransactionResult from "../TransactionResult/TransactionResult";
import "./SimplyConnectDeposit.scss";

type Props = {
  loadUser: () => any;
  user: UserType;
  gateways: [];
  updateGateways: Function;
  paymentMethod: string;
  openOrderPaymentMethod: string;
};

type ButtonEvent =
  | React.MouseEvent<HTMLButtonElement>
  | React.KeyboardEvent<HTMLButtonElement>;

const SimplyConnectDeposit: React.FC<Props> = ({
  loadUser,
  user,
  gateways,
  updateGateways,
  paymentMethod,
  openOrderPaymentMethod,
}) => {
  const [amount, setAmount] = useState("0");
  const [isPayByCardEnabled, setIsPayByCardEnabled] = useState(false);
  const [sessionToken, setSessionToken] = useState("");
  const [isOpenOrderSuccess, setIsOpenOrderSuccess] = useState(false);
  const [isOpenOrderError, setIsOpenOrderError] = useState(false);
  const [isSendingOpenOrder, setIsSendingOpenOrder] = useState(false);
  const [currency, setCurrency] = useState("");
  const [isPayByCardButtonHidden, setIsPayByCardButtonHidden] = useState(false);
  const [merchantId, setMerchantId] = useState("");
  const [merchantSiteId, setMerchantSiteId] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const I18n = NuveiHelpers.nuveiTranslations();
  const [transactionStatus, setTransactionStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isTransactionStatusHidden, setIsTransactionStatusHidden] =
    useState(true);
  const [disableCheckoutForm, setDisableCheckoutForm] = useState(false);
  const [paymentAttemptId, setPaymentAttemptId] = useState(0);
  const [fees, setFees] = useState(0);
  const [minAmount, setMinAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);
  const [displayInteracMessage, setDisplayInteracMessage] = useState(false);

  useEffect(() => {
    loadUser();
    LoadNuveiScript(NuveiHelpers.NuveiSimplyConnectScripts.CHECKOUT);
    checkGateway();
  }, []);

  // check if gateways are empty and fetch them if they are
  const checkGateway = () => {
    if (isEmpty(gateways)) {
      getFetchGlobalGateways();
    } else {
      updateFees();
    }
  };

  const updateFees = () => {
    gateways.forEach((gateway: any) => {
      if (gateway.type === "nuvei") {
        setFees(gateway.depositDetails?.fees?.pct || 0);
        setMinAmount(gateway.depositDetails?.txnLimits?.min);
        setMaxAmount(gateway.depositDetails?.txnLimits?.max);
      }
    });
  };

  /**
   * Fetches global gateways and updates the gateways state.
   * Also updates the fees, minAmount and maxAmount after fetching the gateways.
   * @returns {Promise<void>} A promise that resolves after updating the gateways and fees.
   */
  const getFetchGlobalGateways = async () => {
    fundingAPI
      .fetchGlobalGateways()
      .then(resp => {
        updateGateways(resp.gateways);
        return resp;
      })
      .then(() => {
        console.log("Gateways fetched successfully", gateways);
        updateFees();
      });
  };

  useEffect(() => {
    setIsPayByCardEnabled(Number(amount) > 0);
    if (!isEmpty(user)) {
      if (user?.currencyCode) {
        setCurrency(user.currencyCode);
      }
      if (user?.address?.countryCode) {
        setCountryCode(user?.address?.countryCode);
      }
    }
  }, [amount, user]);

  useEffect(() => {
    if (isOpenOrderSuccess) {
      setIsOpenOrderError(false);
      if (!isSendingOpenOrder) {
        handleCheckout();
        setIsPayByCardButtonHidden(true);
      }
    } else if (isOpenOrderError) {
      setIsOpenOrderSuccess(false);
    }
  }, [isOpenOrderSuccess, isOpenOrderError, isSendingOpenOrder]);

  const openOrder = async (e: any) => {
    e.preventDefault();
    setIsSendingOpenOrder(true);

    try {
      const resp = await nuveiApi.openOrder(
        amount,
        FundingTypes.DEPOSIT,
        currency,
        openOrderPaymentMethod
      );
      setMerchantId(resp.data.merchantId);
      setMerchantSiteId(resp.data.merchantSiteId);
      setSessionToken(resp.data.sessionToken);
      setIsOpenOrderSuccess(true);
      setIsSendingOpenOrder(false);
      setIsOpenOrderError(false);
      setPaymentAttemptId(resp.data.paymentAttemptId);
      if (paymentMethod === NuveiPaymentMethods.INTERAC_E_TRANSFER) {
        setDisplayInteracMessage(true);
      }
      // build up string for logging to grayLog
      // log to grayLog
      NuveiHelpers.logOpenOrderSuccessResponseToGraylog(
        "Deposit open order success",
        resp.data,
        true
      );
    } catch (error) {
      console.log(error);
      setIsOpenOrderError(true);
      setErrorMessage(
        error?.response?.data?.errors[0]?.messages[0]
          ? error.response.data.errors[0].messages[0]
          : error.message
      );
      NuveiHelpers.logOpenOrderErrorResponseToGraylog(
        "Deposit error open order",
        error,
        paymentAttemptId,
        true
      );
    } finally {
      setIsSendingOpenOrder(false);
    }
  };

  const handleChange = (id: string, value: string) =>
    id === "amount" && setAmount(value);

  const handleCheckout = async () => {
    NuveiHelpers.parseAmountToNumber(amount);
    // Determine the environment for testing or production
    const environment = ENV.PRODUCTION === "TRUE" ? "prod" : "int";
    const billingAddress = {
      email: user?.email,
      country: countryCode,
      firstName: user?.firstName,
      lastName: user?.lastName,
      mobilePhone: user?.contactDetails?.phoneNumber,
    };
    // payload for simply connect checkout
    const payload = {
      sessionToken,
      env: environment,
      merchantId,
      merchantSiteId,
      renderTo: "#checkout",
      clientUniqueId: paymentAttemptId,
      amount,
      country: countryCode,
      currency,
      userTokenId: user.userId,
      pmWhitelist: [paymentMethod],
      showResponseMessage: false,
      showCardLogos: false,
      email: user.email,
      billingAddress,
      onResult: function (result) {
        if (result.error) {
          // log error to graylog
          NuveiHelpers.logOpenOrderErrorResponseToGraylog(
            "Deposit result response error",
            result.error,
            paymentAttemptId,
            true
          );
        } else {
          verifyPaymentStatus(result);
          NuveiHelpers.logDepositResultSuccessResponseToGraylog(
            "Deposit result response success",
            result,
            paymentAttemptId
          );
        }
      },
      savePM: false,
    };
    // send simply connect checkout request
    checkout(payload);
  };

  const verifyPaymentStatus = async result => {
    // disable form and display loading spinner during verification API call
    setDisableCheckoutForm(true);

    nuveiApi
      .processDeposit(sessionToken)
      .then(resp => {
        setTransactionStatus(resp.data.status);
        setDisableCheckoutForm(false);
        nuveiApi.processDepositError(
          `Deposit ProcessDeposit Status: ${resp.data.status}`,
          paymentAttemptId
        );
      })
      .catch(error => {
        setTransactionStatus(NuveiTransactionStatus.ERROR);
        setDisableCheckoutForm(false);
        // log error to graylog
        NuveiHelpers.logOpenOrderErrorResponseToGraylog(
          "Deposit processDeposit response error",
          result.error,
          paymentAttemptId,
          true
        );
      });
  };

  useEffect(() => {
    if (!isEmpty(transactionStatus)) {
      // Update the visibility of the transaction status
      // destroying the simply connect checkout instance when completex.
      setIsTransactionStatusHidden(false);
      checkout.destroy();
    }
  }, [transactionStatus]);

  // reset form for new payment
  const newPayment = async (e: any) => {
    e.preventDefault();
    setTransactionStatus("");
    setIsTransactionStatusHidden(true);
    setIsPayByCardButtonHidden(false);
    setIsOpenOrderSuccess(false);
    setIsOpenOrderError(false);
    setIsSendingOpenOrder(false);
    setAmount("0");
    setSessionToken("");
  };

  return (
    <div
      className={`simply-connect-deposit ${
        transactionStatus === NuveiTransactionStatus.APPROVED ? "success" : ""
      } ${
        transactionStatus === NuveiTransactionStatus.DECLINED ? "error" : ""
      } ${transactionStatus === NuveiTransactionStatus.ERROR ? "error" : ""}`}
    >
      {!isPayByCardButtonHidden && (
        <TextInput
          placeholder="Enter amount"
          value={amount}
          type="text"
          id="amount"
          label={I18n.amount}
          onChange={handleChange}
          className={isNaN(Number(amount)) ? "error" : "success"}
        />
      )}

      {isSendingOpenOrder ? (
        <div className="loading-spinner">
          <LoadingSpinner />
        </div>
      ) : (
        !isPayByCardButtonHidden && (
          <>
            <button
              onClick={(e: ButtonEvent) => openOrder(e)}
              disabled={!isPayByCardEnabled}
              className="pay-by-card"
              id={"deposit-button"}
            >
              <div>
                {" "}
                <FormattedMessage tagName="span" id="cashier-pay" />
              </div>
            </button>
          </>
        )
      )}

      {isOpenOrderError && (
        <div className="error-message error">{errorMessage}</div>
      )}
      {!isEmpty(sessionToken) && (
        <>
          <div
            id="checkout"
            className={disableCheckoutForm ? "checkout-form--disabled" : ""}
          >
            {disableCheckoutForm && <LoadingSpinner />}
          </div>
        </>
      )}

      {!isTransactionStatusHidden && (
        <TransactionResult
          user={user}
          transactionStatus={transactionStatus}
          newPayment={newPayment}
          transactionType={FundingTypes.DEPOSIT}
        />
      )}
      {paymentMethod === NuveiPaymentMethods.INTERAC_E_TRANSFER && (
        // formatted the translated string so that "Interac" is bold
        <FormattedMessage
          tagName="div"
          id="cashier-withdrawal-interac-trademark"
          values={{
            b: chunks => <strong>{chunks}</strong>,
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.userModel.user,
  gateways: state.cashierModel.gateways,
});

const mapDispatchToProps = (dispatch: any) => ({
  loadUser: () => {
    dispatch.userModel.loadUser();
  },
  updateGateways: gateways => {
    dispatch.cashierModel.updateGateways(gateways);
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(SimplyConnectDeposit));
